import {useState, useEffect} from "react";
import {Box, Dialog, IconButton, Menu, MenuItem, Stack, Tooltip, Typography} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import SaveIcon from "@mui/icons-material/Save";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import RefreshIcon from "@mui/icons-material/Refresh";
import {Navigate, useParams, useNavigate} from "react-router";
import {appSession} from "../../../TR3AD.ts";
import WidgetHeader from "../../../widgets/WidgetHeader.tsx";
import {appStyles} from "../../../AppStyles.ts";
import type {GraphSettings} from "../../../sdo/GraphSettings.ts";
import {createDefaultGraphSettings} from "../../../sdo/GraphSettings.ts";
import GraphEditForm from "./GraphEditForm.tsx";

// TODO: Replace with API call to GraphManager.readGraphSettings(uid)
// TODO: Drop once appSession.getUsername() returns a real value in dev.
const DEV_USER = "dev-user";

function buildTestGraph(uid: string): GraphSettings {
    const currentUser = appSession.getUsername() ?? DEV_USER;
    if (uid === "new") return {...createDefaultGraphSettings(), uid: `graph-new-${Date.now()}`, createdBy: currentUser};
    return {
        ...createDefaultGraphSettings(),
        uid,
        createdBy: currentUser,
        title: "Entity Network - Q1 2026",
        rootNodes: [
            {uid: "rn-1", display: "John Marcus Riley", typeKey: "PERSON", profileType: "INTEL"},
            {uid: "rn-2", display: "Sentinel Group Holdings", typeKey: "ORGANIZATION", profileType: "INTEL"},
        ],
    };
}

// --- Toolbar button style ---

const toolbarButtonSx = {
    color: "#7093a0",
    border: "1px solid rgba(112, 147, 160, 0.35)",
    borderRadius: "6px",
    padding: "6px",
    "&:hover": {
        color: "#02cfee",
        borderColor: "#02cfee",
        backgroundColor: "rgba(2, 207, 238, 0.08)",
    },
};

const activeToolbarButtonSx = {
    ...toolbarButtonSx,
    color: "#02cfee",
    borderColor: "#02cfee",
    backgroundColor: "rgba(2, 207, 238, 0.1)",
};

// --- Main component ---

function UXGraphViewer() {
    const {networkUID, graphUID} = useParams();
    const navigate = useNavigate();
    const [graph, setGraph] = useState<GraphSettings | null>(null);
    const [showSettings, setShowSettings] = useState(false);
    const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);

    useEffect(() => {
        // TODO: Replace with API call
        setGraph(buildTestGraph(graphUID ?? "new"));
    }, [networkUID, graphUID]);

    function handleSettingsSave(updated: GraphSettings) {
        // TODO: Replace with API call to update graph settings (createdBy unchanged)
        setGraph(updated);
        setShowSettings(false);
    }

    function handleSettingsDelete() {
        if (!graph) return;
        // TODO: Replace with API call to delete graph
        setShowSettings(false);
        navigate(`/networks/${networkUID}/graphs`);
    }

    function handleExport(format: "PNG" | "SVG") {
        // TODO: Render the graph canvas to a blob and trigger a browser download
        // (mirrors desktop UXGraphViewer.onExport / onExportSVG). Wire up once the
        // graph renderer is integrated — it will own the rasterize/serialize logic.
        console.log(`Export as ${format}:`, graph?.uid);
        setExportAnchor(null);
    }

    return (
        <Stack direction="column" width="100%" minHeight="100vh">
            {!appSession.activeSession() ? (
                <Navigate to="/" />
            ) : (
                <Stack direction="column" spacing={1} width="99%">
                    <WidgetHeader
                        title="Graphs"
                        breadcrumbs={[
                            {display: "Networks", linkTo: "/networks/", activeView: false},
                            {display: String(networkUID), linkTo: `/networks/${networkUID}`, activeView: false},
                            {display: "Graphs", linkTo: `/networks/${networkUID}/graphs`, activeView: false},
                            {display: graph?.title ?? "Graph", linkTo: "", activeView: true},
                        ]}
                    />
                    <Stack sx={{...appStyles.sectionBox, flex: 1}} spacing={0} width="100%">
                        {/* Row 1: title left · settings/save/export right */}
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{padding: "8px 12px"}}>
                            <Typography sx={{fontSize: "1rem", fontWeight: 600, color: "#F2F5FA", textAlign: "left"}}>
                                {graph?.title ?? ""}
                            </Typography>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <Tooltip title="Settings">
                                    <IconButton
                                        size="small"
                                        onClick={() => setShowSettings(prev => !prev)}
                                        sx={showSettings ? activeToolbarButtonSx : toolbarButtonSx}
                                    >
                                        <SettingsIcon sx={{fontSize: 18}} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Save graph">
                                    <IconButton size="small" sx={toolbarButtonSx} onClick={() => {
                                        // TODO: Save graph view state (zoom/pan/node positions) via API
                                        console.log("Save graph:", graph?.uid);
                                    }}>
                                        <SaveIcon sx={{fontSize: 18}} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Export">
                                    <IconButton
                                        size="small"
                                        sx={exportAnchor ? activeToolbarButtonSx : toolbarButtonSx}
                                        onClick={(e) => setExportAnchor(e.currentTarget)}
                                    >
                                        <FileDownloadIcon sx={{fontSize: 18}} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>

                        {/* Row 2: centered session-scoped graph controls (darker band to delineate from title row and canvas) */}
                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{padding: "6px 12px", backgroundColor: "rgba(0, 10, 20, 0.55)", borderTop: "1px solid #3F464F", borderBottom: "1px solid #3F464F"}}>
                            <Tooltip title="Expand all">
                                <IconButton size="small" sx={toolbarButtonSx} onClick={() => {
                                    // TODO: Expand all nodes (mirrors desktop UXGraphViewer.onExpandAll)
                                    console.log("Expand all:", graph?.uid);
                                }}>
                                    <UnfoldMoreIcon sx={{fontSize: 18}} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Collapse all">
                                <IconButton size="small" sx={toolbarButtonSx} onClick={() => {
                                    // TODO: Collapse all nodes (mirrors desktop UXGraphViewer.onCollapseAll)
                                    console.log("Collapse all:", graph?.uid);
                                }}>
                                    <UnfoldLessIcon sx={{fontSize: 18}} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Refresh">
                                <IconButton size="small" sx={toolbarButtonSx} onClick={() => {
                                    // TODO: Reload graph data (mirrors desktop UXGraphViewer.onRefresh)
                                    console.log("Refresh:", graph?.uid);
                                }}>
                                    <RefreshIcon sx={{fontSize: 18}} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Zoom in">
                                <IconButton size="small" sx={toolbarButtonSx}>
                                    <ZoomInIcon sx={{fontSize: 18}} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Zoom out">
                                <IconButton size="small" sx={toolbarButtonSx}>
                                    <ZoomOutIcon sx={{fontSize: 18}} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Fit to screen">
                                <IconButton size="small" sx={toolbarButtonSx}>
                                    <FitScreenIcon sx={{fontSize: 18}} />
                                </IconButton>
                            </Tooltip>
                        </Stack>

                        {/* Graph canvas — full width */}
                        <Box sx={{
                            flex: 1,
                            minHeight: 500,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(0, 10, 20, 0.4)",
                            borderRadius: "0 0 10px 10px",
                        }}>
                            {/* TODO: Graph renderer goes here */}
                            <Stack spacing={1} alignItems="center">
                                <Box component="img" src="/images/graph.svg" sx={{width: 64, height: 64, opacity: 0.2}}
                                     onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = "none"; }}
                                />
                                <Typography sx={{fontSize: "0.85rem", color: "#7093a0", opacity: 0.6}}>
                                    Graph canvas — renderer not yet integrated
                                </Typography>
                            </Stack>
                        </Box>
                    </Stack>

                    {/* Export menu */}
                    <Menu
                        anchorEl={exportAnchor}
                        open={!!exportAnchor}
                        onClose={() => setExportAnchor(null)}
                        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                        transformOrigin={{vertical: "top", horizontal: "right"}}
                        slotProps={{paper: {sx: {backgroundColor: "#1a2730", border: "1px solid #3F464F", borderRadius: "8px", minWidth: 140}}}}
                    >
                        <MenuItem onClick={() => handleExport("PNG")} sx={{fontSize: "0.8rem", color: "#F2F5FA", "&:hover": {backgroundColor: "rgba(2, 207, 238, 0.12)", color: "#02cfee"}}}>
                            As PNG
                        </MenuItem>
                        <MenuItem onClick={() => handleExport("SVG")} sx={{fontSize: "0.8rem", color: "#F2F5FA", "&:hover": {backgroundColor: "rgba(2, 207, 238, 0.12)", color: "#02cfee"}}}>
                            As SVG
                        </MenuItem>
                    </Menu>

                    {/* Settings modal */}
                    <Dialog
                        open={showSettings && !!graph}
                        onClose={() => setShowSettings(false)}
                        maxWidth={false}
                        slotProps={{paper: {sx: {backgroundColor: "transparent", boxShadow: "none", overflow: "visible"}}}}
                    >
                        {graph && (
                            <GraphEditForm
                                initial={graph}
                                onSave={handleSettingsSave}
                                onCancel={() => setShowSettings(false)}
                                onDelete={handleSettingsDelete}
                            />
                        )}
                    </Dialog>
                </Stack>
            )}
        </Stack>
    );
}

export default UXGraphViewer;
